import '@gitbook/spine-css/tokens.css';
import '@gitbook/react-uikit/src/theme/css/design-tokens.css';
import '@gitbook/spine-css/preflight.css';
import './app.css';
import { createRoot } from 'react-dom/client';

import { getApplicationEndpoint, releaseVersion, webConfig } from '@gitbook/env';
import { initializeSentry } from '@gitbook/util-debug';
import { head } from '@gitbook/util-react-head';
import { assert } from '@gitbook/util-ts';

import { Application } from './Application';
import { isSentryReplayEnabled } from './Components';
import { preloadChunksForLocation } from './Screens/routePreload';
import { initializeAuth } from './state';

// Load Noto Color Emoji asynchronously so it never blocks CSSOM construction/first paint.
head.writeAsync({
    type: 'link',
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Noto+Color+Emoji&display=swap',
});

let isInitialized = false;

async function main() {
    // Prevent multiple initialization due to hot reloading
    if (isInitialized) {
        return;
    }

    // Track exceptions in Sentry
    initializeSentry({
        dsn: webConfig.sentry.dsn,
        release: releaseVersion,
        projectId: webConfig.firebase.projectId,
        tracesSampling: webConfig.sentry.tracesSampling,
        recordReplays: isSentryReplayEnabled,
        tunnel: getSentryTunnelURL(),
    });

    // Start firebase auth before even starting rendering to avoid async delay
    initializeAuth();

    // Warm the current route's chunk chain in parallel with auth/data
    // requests, instead of waiting for React's render to reach it.
    preloadChunksForLocation(window.location);
    // An overlay's background screen (history v5 stores it under `usr`) also
    // needs its own chunk chain, since it renders behind the overlay.
    const background = (window.history.state as { usr?: { background?: string } } | null)?.usr
        ?.background;
    if (background) {
        const url = new URL(background, window.location.origin);
        preloadChunksForLocation(url);
    }

    isInitialized = true;

    const rootElement = document.getElementById('gitbook-root');
    assert(rootElement, 'Root element not found');
    const root = createRoot(rootElement);
    root.render(<Application />);
}

/**
 * Wait for the DOM to be interactive to continue and call `callback`.
 */
function waitDOMReady(callback: () => void) {
    if (document.readyState === 'interactive' || document.readyState === 'complete') {
        callback();
    } else {
        document.addEventListener('DOMContentLoaded', () => {
            callback();
        });
    }
}

waitDOMReady(() => {
    void main();
});

function getSentryTunnelURL(): string | undefined {
    const url = new URL('/__monitoring/tunnel', getApplicationEndpoint());
    return url.protocol === 'https:' ? url.toString() : undefined;
}
